import { analysePlan, WINDOW_SILL } from './floorPlanVectors';
import { CLASS_TO_ROOM_TYPE } from './roboflowService';
import { ROOM_TYPES, computeRoomAutoWalls } from '../store/houseSlice';

// ── DETECTED PLAN → EDITABLE PROJECT ─────────────────────────────────────────
// floorPlanVectors produces the plan as 2D vectors in metres. This module maps
// those onto the house store's own model — rooms, wall segments and openings —
// so an imported plan lands in the 2D editor as ordinary shapes the user can
// drag, resize, re-type and re-material exactly like hand-placed ones.
//
// ══ UNITS ═══════════════════════════════════════════════════════════════════
//
// analysePlan works in metres centred on the origin. The Builder works in
// INTEGER grid cells starting at 0, where one cell is one metre (House3DView's
// M = 1). So the conversion is a translation and a round — not a rescale.
//
// Rooms are snapped to whole cells because the Builder's wall derivation,
// opening keys and adjacency detection are all defined on the cell lattice.
// A room at x=3.4 would simply not share an edge with its neighbour.

const MARGIN = 2;          // cells of clear space between the plan and the grid edge
// Cells on a room's SHORT side.
//
// A region thinner than this is not made into a room — it is left to be
// described by its walls alone. A one-cell-deep pocket placed as a room reads
// as a "room inside a room": a sliver of floor sitting in the middle of a
// larger space, which is not how anyone draws a plan. The walls around it are
// still emitted, so the space is properly enclosed; it simply has no floor
// object of its own, exactly as if it had been drawn with the wall tool.
const MIN_ROOM = 2;

/**
 * Converts a Roboflow detection into rooms + wall openings for the house store.
 *
 * @param {{image:{width,height}, predictions:Array}} detection
 * @param {object} opts
 * @param {number} opts.scaleFactor   METRES PER PIXEL
 * @param {number} opts.gridSize      grid width/height in cells (see GRID_SIZES)
 * @returns {{rooms:Array, autoWallOpenings:object, nextId:number,
 *            nextWallOpeningId:number, stats:object}}
 */
export function floorPlanToRooms(detection, { scaleFactor, gridSize, imageWalls = null }) {
  const plan = analysePlan(detection, { scaleFactor, imageWalls });

  const empty = {
    rooms: [], autoWallOpenings: {}, nextId: 1, nextWallOpeningId: 1,
    stats: { ...plan.stats, placedRooms: 0, placedOpenings: 0, clamped: 0 },
  };
  if (!plan.rooms.length) return empty;

  // ── translate the plan out of centre-origin space into the grid ──
  const minX = Math.min(...plan.rooms.map(r => Math.min(r.x0, r.x1)));
  const minZ = Math.min(...plan.rooms.map(r => Math.min(r.z0, r.z1)));
  const toCellX = m => Math.round(m - minX) + MARGIN;
  const toCellY = m => Math.round(m - minZ) + MARGIN;

  const labelFor = (type) =>
    ROOM_TYPES.find(t => t.type === type)?.label || 'Room';

  let clamped = 0;
  let nextId = 1;
  const rooms = [];
  for (const r of plan.rooms) {
    const x = toCellX(Math.min(r.x0, r.x1));
    const y = toCellY(Math.min(r.z0, r.z1));
    // Rounded, but never to nothing: a narrow hall measuring 0.8 m across is a
    // real room and must survive as one cell rather than being rounded away.
    let w = Math.max(1, Math.round(Math.abs(r.x1 - r.x0)));
    let h = Math.max(1, Math.round(Math.abs(r.z1 - r.z0)));
    if (w < MIN_ROOM || h < MIN_ROOM) continue;

    // A plan calibrated too large would otherwise place rooms off the grid,
    // where the Builder cannot show or edit them.
    if (x + w > gridSize) { w = gridSize - x; clamped++; }
    if (y + h > gridSize) { h = gridSize - y; clamped++; }
    if (x < 0 || y < 0 || w < MIN_ROOM || h < MIN_ROOM) { clamped++; continue; }

    const type = CLASS_TO_ROOM_TYPE[r.cls] || 'custom';
    rooms.push({ id: nextId++, type, label: labelFor(type), x, y, w, h });
  }
  if (!rooms.length) return { ...empty, stats: { ...empty.stats, clamped } };

  // ── openings ──
  // The Builder derives its own walls from the placed rooms, and an opening is
  // keyed to one of THOSE walls — so the walls are recomputed here with the
  // store's own function rather than reusing the metre-space segments. Anything
  // else would produce keys the Builder does not recognise, and the openings
  // would silently not render.
  const walls = computeRoomAutoWalls(rooms);
  const autoWallOpenings = {};
  let nextWallOpeningId = 1;
  let placedOpenings = 0;

  // The openings the preview drew — read off the drawing and typed by the
  // detector — rather than the raw detections. Using a different set here is
  // why the created project used to disagree with its own preview.
  const planOpenings = plan.segments.some(s => s.openings?.length)
    ? plan.segments.flatMap(s => (s.openings || []).map(o => ({
      type: o.type,
      x: s.axis === 'h' ? (o.from + o.to) / 2 : s.at,
      z: s.axis === 'h' ? s.at : (o.from + o.to) / 2,
    })))
    : plan.openings;

  for (const o of planOpenings) {
    const ox = toCellX(o.x);
    const oy = toCellY(o.z);

    let best = null, bestDist = Infinity;
    for (const w of walls) {
      const isH = w.y1 === w.y2;
      const along = isH ? ox : oy;
      const lo = isH ? Math.min(w.x1, w.x2) : Math.min(w.y1, w.y2);
      const hi = isH ? Math.max(w.x1, w.x2) : Math.max(w.y1, w.y2);
      if (along < lo || along > hi) continue;
      const dist = Math.abs((isH ? oy : ox) - (isH ? w.y1 : w.x1));
      if (dist < bestDist) { bestDist = dist; best = { w, isH, along, lo, hi }; }
    }
    // Generous, for the reason given in floorPlanVectors: a door's box is inflated
    // by its swing arc, so its centre sits off the wall it belongs to.
    if (!best || bestDist > 2) continue;

    const len = best.hi - best.lo;
    if (len <= 0) continue;
    const key = best.isH
      ? `h:${best.w.y1}:${best.lo}:${best.hi}`
      : `v:${best.w.x1}:${best.lo}:${best.hi}`;

    // t is the 0–1 fraction along the wall (House3DView: worldX = X1 + t*len).
    const t = Math.min(0.95, Math.max(0.05, (best.along - best.lo) / len));

    if (!autoWallOpenings[key]) autoWallOpenings[key] = [];
    autoWallOpenings[key].push({
      id: nextWallOpeningId++,
      type: o.type,
      t,
      // Sizes come from the store's own defaults rather than the detection —
      // see openingSpan in floorPlanVectors for why a detected box is a poor
      // measurement of a door.
      ...(o.type === 'window' ? { sill: WINDOW_SILL } : {}),
    });
    placedOpenings++;
  }

  return {
    rooms,
    autoWallOpenings,
    nextId,
    nextWallOpeningId,
    stats: {
      ...plan.stats,
      placedRooms: rooms.length,
      placedOpenings,
      clamped,
    },
  };
}

// The saved-project file version, as written by every other save path
// (guestDesign.saveGuestDesign, Builder/RoomEditor/House3DView handleWebSave).
const FILE_VERSION = 1;

/**
 * The conversion, packaged as a saved-design document.
 *
 * ══ ROOMS *AND* WALLS ═══════════════════════════════════════════════════════
 *
 * Both, because a plan needs both. Rooms give the floors, labels, materials and
 * the whole per-room design flow; walls carry the geometry rooms cannot express
 * — a partition that does not enclose anything, a stub beside a doorway.
 *
 * ══ WHY THE ROOMS NO LONGER OVERLAP ═════════════════════════════════════════
 *
 * Rooms live on the Builder's whole-metre lattice, and an earlier version
 * rounded each room's x, y, width and height independently. That is what
 * produced overlapping rooms and the tiny box adrift inside another room:
 * rounding a shared edge twice, once from each side, can easily send the two
 * copies to different cells.
 *
 * Here the COORDINATE is rounded, never the size. Two rooms that met at 6.4 m
 * both map that same 6.4 to cell 6, so they still meet — exactly, with no gap
 * and no overlap — and each room's size simply falls out of its rounded
 * corners. Sharing edges is preserved by construction rather than hoped for.
 */
export function floorPlanToDesign(detection, { scaleFactor, gridSize, imageWalls = null }) {
  const plan = analysePlan(detection, { scaleFactor, imageWalls });
  const segs = plan.segments || [];
  if (!segs.length && !plan.rooms.length) return null;

  // ── one lattice, shared by everything ──
  const xs = segs.flatMap(s => (s.axis === 'v' ? [s.at] : [s.from, s.to]))
    .concat(plan.rooms.flatMap(r => [r.x0, r.x1]));
  const zs = segs.flatMap(s => (s.axis === 'h' ? [s.at] : [s.from, s.to]))
    .concat(plan.rooms.flatMap(r => [r.z0, r.z1]));
  if (!xs.length || !zs.length) return null;
  const minX = Math.min(...xs), minZ = Math.min(...zs);
  const cx = v => Math.round(v - minX) + MARGIN;
  const cz = v => Math.round(v - minZ) + MARGIN;
  const inGrid = v => v >= 0 && v <= gridSize;

  const labelFor = type => ROOM_TYPES.find(t => t.type === type)?.label || 'Room';

  // ── rooms ──
  let nextId = 1;
  const rooms = [];
  for (const r of plan.rooms) {
    const x = cx(Math.min(r.x0, r.x1)), y = cz(Math.min(r.z0, r.z1));
    const w = cx(Math.max(r.x0, r.x1)) - x, h = cz(Math.max(r.z0, r.z1)) - y;
    if (w < MIN_ROOM || h < MIN_ROOM) continue;
    if (!inGrid(x) || !inGrid(y) || !inGrid(x + w) || !inGrid(y + h)) continue;
    const type = CLASS_TO_ROOM_TYPE[r.cls] || 'custom';

    // A COMPOUND ROOM WHEN THE SPACE IS NOT A RECTANGLE.
    //
    // A living area routinely wraps around a hall or a cupboard, and its
    // bounding box then covers that neighbour completely — which is how a small
    // room ended up buried inside a larger one, and why the two overlapped.
    // The store already models this: `rects` lets one room occupy an L, so the
    // room covers the cells it really has and nothing else.
    // RASTERISED onto the grid, not rounded cell by cell.
    //
    // The region's own cells are sub-metre — they are cut by every wall line in
    // the plan — so rounding each one's two edges independently collapses any
    // cell narrower than a metre to zero width, and it gets dropped. A room made
    // mostly of such cells then survives as a thin sliver of its real self,
    // which is what turned the hall into a 4 x 1 strip.
    //
    // Testing each WHOLE grid cell for coverage instead gives exact, gapless
    // occupancy at the resolution the Builder actually works in.
    const covered = (X, Y) => {
      const mx = X + 0.5 - MARGIN + minX;      // this cell's centre, in metres
      const mz = Y + 0.5 - MARGIN + minZ;
      return (r.parts || []).some(q =>
        mx >= Math.min(q.x0, q.x1) && mx <= Math.max(q.x0, q.x1)
        && mz >= Math.min(q.z0, q.z1) && mz <= Math.max(q.z0, q.z1));
    };

    let rects = [];
    for (let Y = y; Y < y + h; Y++) {
      // Runs of covered cells on a row become one rect...
      let runStart = null;
      for (let X = x; X <= x + w; X++) {
        const on = X < x + w && covered(X, Y);
        if (on && runStart === null) runStart = X;
        else if (!on && runStart !== null) {
          rects.push({ x: runStart, y: Y, w: X - runStart, h: 1 });
          runStart = null;
        }
      }
    }
    // ...and identical runs stacked on consecutive rows merge into one, so a
    // plain rectangular room comes out as a single rect instead of a stack of
    // one-metre strips.
    rects = rects.reduce((acc, rc) => {
      const above = acc.find(a => a.x === rc.x && a.w === rc.w && a.y + a.h === rc.y);
      if (above) { above.h += rc.h; return acc; }
      acc.push(rc);
      return acc;
    }, []);

    // A room whose cells all fell outside the grid is not placeable.
    if (!rects.length) continue;

    const room = { id: nextId++, type, label: labelFor(type), x, y, w, h };
    // `rects` only when the room is genuinely not its bounding box — a simple
    // room stays simple, keeping every editor on its existing fast path.
    const coversBox = rects.length === 1
      && rects[0].x === x && rects[0].y === y && rects[0].w === w && rects[0].h === h;
    if (!coversBox) room.rects = rects;
    rooms.push(room);
  }

  // ── walls, on the same lattice ──
  const snapped = [];
  for (const s of segs) {
    const seg = s.axis === 'h'
      ? { axis: 'h', at: cz(s.at), from: cx(s.from), to: cx(s.to) }
      : { axis: 'v', at: cx(s.at), from: cz(s.from), to: cz(s.to) };
    if (seg.to - seg.from < 1) continue;                      // rounded away
    if (![seg.at, seg.from, seg.to].every(inGrid)) continue;
    seg.openings = (s.openings || []).map(o => ({
      type: o.type,
      at: s.axis === 'h' ? cx((o.from + o.to) / 2) : cz((o.from + o.to) / 2),
      width: Math.abs(o.to - o.from),
    }));
    snapped.push(seg);
  }

  // A room already draws a wall along its own outer edge, so a wall segment
  // lying there would be a second wall in the same place — the exact thing the
  // Builder's overlap check exists to prevent.
  const onRoomEdge = (seg) => rooms.some(room =>
    (room.rects || [{ x: room.x, y: room.y, w: room.w, h: room.h }]).some((r) => {
      if (seg.axis === 'h') {
        if (seg.at !== r.y && seg.at !== r.y + r.h) return false;
        return seg.from < r.x + r.w && r.x < seg.to;
      }
      if (seg.at !== r.x && seg.at !== r.x + r.w) return false;
      return seg.from < r.y + r.h && r.y < seg.to;
    }));

  // ── openings ──
  // Those on a room's own wall go into autoWallOpenings, keyed to the wall the
  // Builder derives from the rooms. The rest ride on their free wall segment.
  const roomWalls = computeRoomAutoWalls(rooms);
  const autoWallOpenings = {};
  let nextWallOpeningId = 1;
  let doors = 0, windows = 0;

  const placeOnRoomWall = (o, seg) => {
    let best = null, bestDist = Infinity;
    for (const w of roomWalls) {
      const isH = w.y1 === w.y2;
      if (isH !== (seg.axis === 'h')) continue;
      const lo = isH ? Math.min(w.x1, w.x2) : Math.min(w.y1, w.y2);
      const hi = isH ? Math.max(w.x1, w.x2) : Math.max(w.y1, w.y2);
      if (o.at < lo || o.at > hi) continue;
      const dist = Math.abs((isH ? w.y1 : w.x1) - seg.at);
      if (dist < bestDist) { bestDist = dist; best = { w, isH, lo, hi }; }
    }
    if (!best || bestDist > 1) return false;
    const len = best.hi - best.lo;
    if (len <= 0) return false;
    const key = best.isH
      ? `h:${best.w.y1}:${best.lo}:${best.hi}`
      : `v:${best.w.x1}:${best.lo}:${best.hi}`;
    if (!autoWallOpenings[key]) autoWallOpenings[key] = [];
    autoWallOpenings[key].push({
      id: nextWallOpeningId++,
      type: o.type,
      t: Math.min(0.95, Math.max(0.05, (o.at - best.lo) / len)),
      width: Math.min(3, Math.max(0.5, o.width)),
      ...(o.type === 'window' ? { sill: WINDOW_SILL } : {}),
    });
    return true;
  };

  const wallSegments = [];
  let nextWallId = 1;
  for (const seg of snapped) {
    const isRoomEdge = onRoomEdge(seg);
    const leftovers = [];
    for (const o of seg.openings) {
      if (o.type === 'window') windows++; else doors++;
      if (isRoomEdge && placeOnRoomWall(o, seg)) continue;
      leftovers.push(o);
    }
    // The wall itself is only added when no room already provides it.
    if (isRoomEdge) {
      // Its openings still have to land somewhere; if the room wall would not
      // take them they are kept on a segment so they are not silently lost.
      if (!leftovers.length) continue;
    }
    const line = seg.axis === 'h'
      ? { x1: seg.from, y1: seg.at, x2: seg.to, y2: seg.at }
      : { x1: seg.at, y1: seg.from, x2: seg.at, y2: seg.to };
    const len = Math.hypot(line.x2 - line.x1, line.y2 - line.y1);
    if (len < 1) continue;
    const lo = seg.axis === 'h' ? line.x1 : line.y1;
    wallSegments.push({
      id: nextWallId++,
      ...line,
      openings: leftovers.map(o => ({
        id: nextWallOpeningId++,
        type: o.type,
        t: Math.min(0.95, Math.max(0.05, (o.at - lo) / len)),
        width: Math.min(3, Math.max(0.5, o.width)),
        ...(o.type === 'window' ? { sill: WINDOW_SILL } : {}),
      })),
    });
  }

  if (!rooms.length && !wallSegments.length) return null;

  return {
    stats: {
      ...plan.stats,
      placedRooms: rooms.length,
      wallSegments: wallSegments.length,
      placedDoors: doors,
      placedWindows: windows,
    },
    design: {
      version: FILE_VERSION,
      savedAt: new Date().toISOString(),
      phase: 'build',
      rooms,
      designs: {},
      furniture: [],
      lighting: [],
      wallSegments,
      autoWallOpenings,
      wallHeight: 2.8,
      nextId,
      nextFurnitureId: 1,
      nextLightingId: 1,
      nextOpeningId: 1,
      nextWallId,
      nextWallOpeningId,
    },
  };
}
